import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { CircularProgress, IconButton, Tooltip } from '@material-ui/core';
import { BarChart as BarIcon, ShowChart, Timeline } from '@material-ui/icons';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHANNELS, TILE_NAMES } from '../utils/rates';
import { METRIC_COLORS } from '../utils/graph';
import { BLUE, BORDER, GREY_TEXT, RESEND_COLOR } from '../AnalyticsV2.styled';
import { Placeholder, Section } from './MessageDetail.styled';

const CHART_HEIGHT = 240;
const AXIS_TICK = { fontSize: 11, fill: GREY_TEXT };
// The paper plane of the MUI Send icon (24×24), drawn at half size.
const PLANE_PATH = 'M2.01 21L23 12 2.01 3 2 10l15 2-15 2z';

const TabRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid ${BORDER};
`;

const Tab = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 4px;
  border-bottom: 2px solid ${BLUE};
  margin-bottom: -1px;
  font-size: 14px;
  color: ${BLUE};
`;

const ChartTypes = styled.div`
  display: flex;
  gap: 4px;
  padding-bottom: 6px;
`;

const ChartTypeButton = styled(IconButton)`
  &.MuiIconButton-root {
    padding: 3px;
    border-radius: 4px;
    background: ${({ $isActive }) => ($isActive ? BLUE : 'transparent')};
    color: ${({ $isActive }) => ($isActive ? '#ffffff' : GREY_TEXT)};
  }
  &.MuiIconButton-root:hover {
    background: ${({ $isActive }) => ($isActive ? BLUE : '#e9ebee')};
  }
  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

const ShowingFor = styled.div`
  margin: 14px 0 12px;
  font-size: 12px;
  color: ${GREY_TEXT};
  b {
    font-weight: 500;
    color: #1c1c1c;
  }
`;

// The line width is set here, not with Line's strokeWidth prop: the global
// rule in Dashboard.css sets every SVG stroke with a hex color to 1, and CSS
// beats the SVG attribute.
const ChartBox = styled.div`
  padding: 16px 16px 8px 0;
  border-radius: 8px;
  background: #ffffff;
  .recharts-line-curve {
    stroke-width: 3;
  }
`;

const formatDay = unixSeconds => moment.unix(unixSeconds).format('MM/DD');
const formatPercent = value => `${value.toFixed(1)}%`;

// The resend icon at the top of the resend line: a green circle with a paper
// plane. Hovering shows `title` in the same tooltip as the rest of the page.
const ResendMarker = ({ viewBox, title }) => (
  <Tooltip title={title} placement="top">
    <g transform={`translate(${viewBox.x}, ${viewBox.y})`}>
      <circle r={11} fill="#ffffff" stroke={RESEND_COLOR} strokeWidth={1.5} />
      <path
        d={PLANE_PATH}
        transform="translate(-6, -6) scale(0.5)"
        fill={RESEND_COLOR}
      />
    </g>
  </Tooltip>
);

// The resends to mark, one marker per day: [{ day, labels }], where day is
// the local midnight (unix seconds) and labels lists every resend on it.
const getResendMarkers = resends => {
  const labelsByDay = new Map();
  for (const { time, label } of resends) {
    const day = moment.unix(time).startOf('day').unix();
    labelsByDay.set(day, [...(labelsByDay.get(day) || []), label]);
  }
  return [...labelsByDay].map(([day, labels]) => ({ day, labels }));
};

// The Engagement graph card. points: from getDailySeries or
// getCombinedDailySeries. resends: [{ time, label }] to mark (time in unix
// seconds); several on one day share one marker, whose tooltip lists them.
// noDataText: "No data yet" / "No data" instead of the graph, or null.
// isError: the per-day request failed.
const EngagementGraph = ({
  channel,
  metrics,
  chartType,
  onChartTypeChange,
  points,
  isLoading,
  isError,
  noDataText,
  resends,
}) => {
  const channelLabel = CHANNELS.find(({ key }) => key === channel).label;
  const markers = getResendMarkers(resends).filter(({ day }) =>
    points.some(point => point.date === day)
  );

  const renderChart = () => {
    if (noDataText)
      return <Placeholder $height={CHART_HEIGHT}>{noDataText}</Placeholder>;
    if (isLoading) {
      return (
        <Placeholder $height={CHART_HEIGHT}>
          <CircularProgress style={{ color: BLUE }} />
        </Placeholder>
      );
    }
    if (isError) {
      return (
        <Placeholder $height={CHART_HEIGHT}>
          {"Couldn't load the graph. Please try again."}
        </Placeholder>
      );
    }

    const Chart = chartType === 'bar' ? BarChart : LineChart;
    return (
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <Chart data={points} margin={{ top: 24, right: 8, left: 0 }}>
          <CartesianGrid
            stroke={BORDER}
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tickFormatter={formatDay}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: BORDER }}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis
            tickFormatter={value => `${value}%`}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={48}
            allowDecimals={false}
          />
          <ChartTooltip
            formatter={(value, metric) => [
              formatPercent(value),
              TILE_NAMES[metric],
            ]}
            labelFormatter={date => moment.unix(date).format('MM/DD/YYYY')}
          />
          <Legend
            formatter={metric => TILE_NAMES[metric]}
            iconType={chartType === 'bar' ? 'square' : 'plainline'}
            wrapperStyle={{ fontSize: 12, color: '#1c1c1c' }}
          />
          {metrics.map(metric =>
            chartType === 'bar' ? (
              <Bar
                key={metric}
                dataKey={metric}
                fill={METRIC_COLORS[metric]}
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              />
            ) : (
              <Line
                key={metric}
                dataKey={metric}
                type="linear"
                stroke={METRIC_COLORS[metric]}
                // A single day has no line to draw, so it shows a dot.
                dot={
                  points.length === 1
                    ? { r: 4, fill: METRIC_COLORS[metric] }
                    : false
                }
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            )
          )}
          {markers.map(({ day, labels }) => (
            <ReferenceLine
              key={day}
              x={day}
              stroke={RESEND_COLOR}
              strokeDasharray="2 3"
              label={
                <ResendMarker
                  title={labels.map((label, index) => (
                    <div key={index}>{label}</div>
                  ))}
                />
              }
            />
          ))}
        </Chart>
      </ResponsiveContainer>
    );
  };

  return (
    <Section>
      <TabRow>
        <Tab>
          <Timeline fontSize="small" /> Engagement
        </Tab>
        <ChartTypes>
          <Tooltip title="Line graph">
            <ChartTypeButton
              aria-label="Line graph"
              aria-pressed={chartType === 'line'}
              $isActive={chartType === 'line'}
              onClick={() => onChartTypeChange('line')}
            >
              <ShowChart />
            </ChartTypeButton>
          </Tooltip>
          <Tooltip title="Bar graph">
            <ChartTypeButton
              aria-label="Bar graph"
              aria-pressed={chartType === 'bar'}
              $isActive={chartType === 'bar'}
              onClick={() => onChartTypeChange('bar')}
            >
              <BarIcon />
            </ChartTypeButton>
          </Tooltip>
        </ChartTypes>
      </TabRow>
      <ShowingFor>
        Showing stats for:{' '}
        <b>
          {channelLabel} -{' '}
          {metrics.map(metric => TILE_NAMES[metric]).join(', ')}
        </b>
      </ShowingFor>
      <ChartBox>{renderChart()}</ChartBox>
    </Section>
  );
};

export default EngagementGraph;
